import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import DivisionFormatGuardCard, { divisionNeedsFormat } from "../components/DivisionFormatGuardCard";
import FieldHint from "../components/FieldHint";
import OfficialRankingCard from "../components/OfficialRankingCard";
import TournamentSetupProgress from "../components/TournamentSetupProgress";
import {
  api,
  ApiError,
  participantDisplayName,
  type OfficialListEntry,
  type ParticipantSeedItemInput,
  type Tournament,
  type TournamentDivision,
  type RegistrationOrder,
  type TournamentParticipant,
} from "../lib/api";
import { ordersByCoveredParticipant } from "../lib/orderCoverage";
import { escapeHtml, openPrintWindow } from "../lib/printTemplate";
import {
  findDuplicateRanks,
  findRankGaps,
  formatDuplicateRanksMessage,
} from "../lib/rankDuplicates";

export type RankingMode = "manual" | "points";

/** Seeds 1..K generados por puntos: solo a quien tiene puntos > 0 (#389);
 * empates de puntos por seed guardado y luego por nombre. `pointsOf`
 * devuelve -1 para capturas inválidas (quedan S/R). */
export function seedsFromPoints(
  participants: TournamentParticipant[],
  pointsOf: (participant: TournamentParticipant) => number,
): Map<number, number> {
  const rows = participants.map((participant) => ({ participant, points: pointsOf(participant) }));
  rows.sort((a, b) => {
    if (a.points !== b.points) return b.points - a.points;
    const rankA = participantRankValue(a.participant);
    const rankB = participantRankValue(b.participant);
    if (rankA !== rankB) return rankA - rankB;
    return participantDisplayName(a.participant).localeCompare(participantDisplayName(b.participant), "es-MX");
  });
  const map = new Map<number, number>();
  let next = 1;
  for (const row of rows) {
    if (row.points > 0) map.set(row.participant.id, next++);
  }
  return map;
}

function savedSeed(participant: Pick<TournamentParticipant, "initial_rank" | "tournament_rank">): number | null {
  return participant.initial_rank ?? participant.tournament_rank ?? null;
}

/** Jugadores cuyo seed GUARDADO no es el que dan sus puntos GUARDADOS. */
export function seedsNotFromPoints(participants: TournamentParticipant[]): number {
  const generated = seedsFromPoints(participants, (participant) => participant.ranking_points ?? 0);
  return participants.filter((participant) => savedSeed(participant) !== (generated.get(participant.id) ?? null)).length;
}

/** ¿Los seeds guardados salen de los puntos guardados? Mismo ORDEN que los
 * puntos (un hueco por una baja o un cambio de categoría no cuenta: al
 * guardar por puntos se compacta 1..K) y ningún seed a quien no tiene puntos. */
export function seedsFollowPoints(participants: TournamentParticipant[]): boolean {
  const generated = seedsFromPoints(participants, (participant) => participant.ranking_points ?? 0);
  const ordered = participants
    .filter((participant) => generated.has(participant.id))
    .sort((a, b) => (generated.get(a.id) ?? 0) - (generated.get(b.id) ?? 0));
  let last = 0;
  for (const participant of ordered) {
    const seed = savedSeed(participant);
    if (seed == null || seed <= last) return false;
    last = seed;
  }
  return participants.every((participant) => generated.has(participant.id) || savedSeed(participant) == null);
}

/** Eduardo 2026-09-26: si la categoría ya tiene puntos capturados, el paso
 * Ranking abre en "Por puntos" (decisión de producto): el seed sale de los
 * puntos y quien no tiene puntos queda S/R. Si aún hay seeds guardados que no
 * salen de los puntos, la pantalla lo avisa y ofrece guardar el orden por
 * puntos (nada se escribe sin esa acción o "Guardar y continuar"). Sin puntos
 * pero con seeds → manual. Sin puntos ni seeds (categoría nueva en el
 * recorrido) conserva el modo anterior. */
export function inferRankingMode(participants: TournamentParticipant[], previous: RankingMode): RankingMode {
  const anyPoints = participants.some((participant) => (participant.ranking_points ?? 0) > 0);
  if (anyPoints) return "points";
  if (participants.some((participant) => savedSeed(participant) != null)) return "manual";
  return previous;
}

/** Celda "Rank actual" (Eduardo 2026-09-25: con puntos/seed capturados no
 * debe decir S/R). `current_rank` es la posición viva y SOLO la escribe la
 * tabla del torneo (gate #122): este paso nunca la guarda. Mientras la
 * categoría no tiene partidos jugados (`hasResults === false`) se MUESTRA el
 * seed —el generado por puntos o el capturado— rotulado como tal, aunque
 * exista un current_rank de la inscripción; no se escribe nada. Si no se
 * pudo saber (`null`), manda la posición viva si existe. */
export function rankActualLabel(
  participant: Pick<TournamentParticipant, "current_rank" | "ranking_points">,
  seed: number | null,
  mode: RankingMode,
  hasResults: boolean | null = null,
): { rank: string; note: string | null; points: string | null } {
  // En modo puntos la columna Puntos ya muestra el valor; en manual solo si hay.
  const points = mode === "manual" && (participant.ranking_points ?? 0) > 0 ? `${participant.ranking_points} pts` : null;
  if (hasResults !== false && participant.current_rank != null) {
    return { rank: `#${participant.current_rank}`, note: null, points };
  }
  if (seed != null) return { rank: `#${seed}`, note: mode === "points" ? "según puntos" : "según seed", points };
  return { rank: "S/R", note: null, points };
}

type RankDraft = Record<number, string>;
type PointsDraft = Record<number, string>;

function parsePositiveRank(raw: string): number | null | "invalid" {
  const value = raw.trim();
  if (!value) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return "invalid";
  return Math.trunc(parsed);
}

export function parsePoints(raw: string): number | "invalid" {
  const value = raw.trim();
  if (!value) return 0;
  // Acepta decimales con punto o coma (82.5 / 82,5); se guardan a 2 decimales.
  if (!/^\d+(?:[.,]\d*)?$|^[.,]\d+$/.test(value)) return "invalid";
  const parsed = Number(value.replace(",", "."));
  if (!Number.isFinite(parsed) || parsed < 0) return "invalid";
  return Math.round(parsed * 100) / 100;
}

function participantRankValue(participant: TournamentParticipant): number {
  // Ordena por SEED (lo que edita este paso), no por posición viva.
  return (
    participant.initial_rank ??
    participant.tournament_rank ??
    Number.MAX_SAFE_INTEGER
  );
}

export function participantSeedInputValue(
  participant: Pick<TournamentParticipant, "initial_rank" | "tournament_rank" | "current_rank">,
): string {
  return String(participant.initial_rank ?? participant.tournament_rank ?? "");
}

function participantCategoryLabel(participant: TournamentParticipant): string {
  return participant.division?.name ?? participant.current_category?.name ?? "General";
}


function csvEscape(value: string | number | null | undefined): string {
  const text = value == null ? "" : String(value);
  if (!/[",\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

export function parseCsvLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === "," && !quoted) {
      cells.push(current);
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current);
  return cells;
}

function downloadBlob(filename: string, content: BlobPart, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function safeFilename(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "torneo";
}

function paymentLabelForOrder(order: RegistrationOrder | null | undefined): string {
  if (!order) return "Pendiente de pago";
  if (order.status === "paid") return "Pago confirmado";
  if (order.status === "payment_sent") return "Pago enviado";
  if (order.status === "refunded") return "Reembolsado";
  if (order.status === "cancelled") return "Cancelado";
  return "Pendiente de pago";
}

function paymentKeyForOrder(order: RegistrationOrder | null | undefined): string {
  if (!order) return "pending";
  if (order.status === "paid") return "paid";
  if (order.status === "payment_sent") return "sent";
  return order.status || "pending";
}

type PdfRow = {
  rank: string;
  player: string;
  category: string;
  /** Club / Academia del jugador (Eduardo 2026-09-23, para Luis Ángel). */
  club: string;
  /** Solo en ranking por puntos: los puntos capturados por el admin. */
  points: string | null;
};

/** Hoja imprimible del ranking con el template compartido de la marca
 * (mismo look & feel que reglamento/brackets — feedback Eduardo 2026-07-15:
 * el PDF crudo hecho a mano "sale muy feo"). */
/** Club / Academia del participante: en dobles, los clubes de la pareja
 * (sin repetir), no solo `participant.player` (revisión Hermes 2026-09-24). */
export function participantClubLabel(participant: {
  player?: { club_name?: string | null } | null;
  team?: { members?: Array<{ player?: { club_name?: string | null } | null }> | null } | null;
}): string {
  const clubs = [
    participant.player?.club_name,
    ...(participant.team?.members ?? []).map((member) => member.player?.club_name),
  ]
    .map((value) => (value ?? "").trim())
    .filter((value) => value.length > 0);
  return [...new Set(clubs)].join(" / ") || "-";
}

/** PDF por puntos (Eduardo 2026-09-26): el número de lista solo lo lleva
 * quien tiene puntos; sin puntos = S/R, igual que la pantalla (#389). La
 * lista llega ordenada por puntos de mayor a menor. */
export function pointsListRankLabel(points: number, index: number): string {
  return points > 0 ? `#${index + 1}` : "S/R";
}

export function rankingSheetBodyHtml(rows: PdfRow[], options: { withPoints?: boolean } = {}): string {
  // Eduardo 2026-09-23: fuera WTN, Rank FMT y Pago; entra Club / Academia
  // después del nombre y, en ranking por puntos, la columna Puntos.
  const withPoints = Boolean(options.withPoints);
  const bodyRows = rows
    .map(
      (row) => `<tr>
        <td class="ranking-print-rank">${escapeHtml(row.rank)}</td>
        <td>${escapeHtml(row.player)}</td>
        <td>${escapeHtml(row.club)}</td>
        <td>${escapeHtml(row.category)}</td>${withPoints ? `
        <td class="ranking-print-points">${escapeHtml(row.points ?? "-")}</td>` : ""}
      </tr>`,
    )
    .join("\n");
  return `<table>
    <thead>
      <tr><th>Rank</th><th>Jugador</th><th>Club / Academia</th><th>Categoría</th>${withPoints ? "<th>Puntos</th>" : ""}</tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>`;
}

const RANKING_SHEET_CSS = `
  .ranking-print-rank { font-weight: 800; white-space: nowrap; }
  .ranking-print-points { font-weight: 800; white-space: nowrap; text-align: right; }
`;

/** Payload de guardado del paso 6. Este paso administra ÚNICAMENTE el seed
 * (`initial_rank`/`tournament_rank`): `current_rank` es la posición viva y
 * pertenece en exclusiva a compute_standings + persist_current_ranks_from_payload
 * (gate de Hermes en #122: escribirlo aquí pisaba la posición viva con el
 * seed al re-guardar). Devuelve null cuando no hay nada que actualizar. */
export function buildSeedUpdatePayload(
  // current_rank está en la firma A PROPÓSITO: se recibe y se ignora — el
  // tipo de retorno garantiza que nunca viaja en el payload.
  participant: Pick<TournamentParticipant, "initial_rank" | "tournament_rank" | "current_rank">,
  parsedRank: number | null,
): { initial_rank: number | null; tournament_rank: number | null } | null {
  if (parsedRank === null) {
    // S/R: limpiar el seed solo si había uno. La posición viva no se toca.
    if (participant.initial_rank == null && participant.tournament_rank == null) {
      return null;
    }
    return { initial_rank: null, tournament_rank: null };
  }
  if (parsedRank === participant.initial_rank && parsedRank === participant.tournament_rank) {
    return null;
  }
  return { initial_rank: parsedRank, tournament_rank: parsedRank };
}

export function buildPointsAndSeedUpdatePayload(
  participant: Pick<TournamentParticipant, "initial_rank" | "tournament_rank" | "current_rank" | "ranking_points">,
  parsedPoints: number,
  generatedRank: number | null,
): { ranking_points?: number; initial_rank?: number | null; tournament_rank?: number | null } | null {
  const seedPayload = buildSeedUpdatePayload(participant, generatedRank);
  const pointsChanged = parsedPoints !== (participant.ranking_points ?? 0);
  if (!seedPayload && !pointsChanged) return null;
  return {
    ...(pointsChanged ? { ranking_points: parsedPoints } : {}),
    ...(seedPayload ?? {}),
  };
}

/** Draft 1..N para una lista ya ordenada (rank random / auto-rankeos): por
 * construcción NUNCA produce duplicados — candado del bug prod 2026-07-22. */
export function buildSequentialRankDraft(orderedParticipantIds: number[]): Record<number, string> {
  const draft: Record<number, string> = {};
  orderedParticipantIds.forEach((participantId, index) => {
    draft[participantId] = String(index + 1);
  });
  return draft;
}

/** Draft desde el ranking FMT: 1..N consecutivo para quienes tienen posición
 * oficial (ordenados por ella), vacío (S/R) para el resto. Sin duplicados. */
export function buildFmtRankDraft(
  rows: { id: number; position: number | null }[],
): Record<number, string> {
  const draft: Record<number, string> = {};
  rows.forEach((row) => {
    draft[row.id] = "";
  });
  rows
    .filter((row): row is { id: number; position: number } => row.position != null)
    .sort((a, b) => a.position - b.position)
    .forEach((row, index) => {
      draft[row.id] = String(index + 1);
    });
  return draft;
}

export function selectOfficialRankingLink<T extends { division_id: number | null }>(
  links: T[],
  divisionId: number | null,
): T | undefined {
  return links.find((link) => link.division_id === divisionId)
    ?? links.find((link) => link.division_id === null);
}

export default function AdminTournamentRankingPage() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = id ? Number(id) : NaN;
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [divisions, setDivisions] = useState<TournamentDivision[]>([]);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [registrationOrders, setRegistrationOrders] = useState<RegistrationOrder[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>(null);
  const { askConfirm, currentSignal } = useAbortableConfirm([tournamentId, selectedDivisionId]);
  const [mode, setMode] = useState<RankingMode>("manual");
  const [rankInputs, setRankInputs] = useState<RankDraft>({});
  const [pointsInputs, setPointsInputs] = useState<PointsDraft>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [gateRefreshKey, setGateRefreshKey] = useState(0);
  const [organizerLogoUrl, setOrganizerLogoUrl] = useState<string | null>(null);
  // Partidos jugados por participante (tabla del torneo): decide si "Rank
  // actual" ya es posición viva o todavía se muestra el seed. null = no se supo.
  const [playedByParticipant, setPlayedByParticipant] = useState<Map<number, number> | null>(null);
  // Blur mientras se guardaba: se re-intenta al terminar (misma categoría).
  const pendingAutosaveRef = useRef<{ divisionId: number | null } | null>(null);
  // Modo antes de sembrar desde la publicación: si la siembra falla, se restaura.
  const modeBeforeSeedingRef = useRef<RankingMode | null>(null);
  // Guardado de seeds en vuelo (autoguardado de puntos o "Guardar y
  // continuar"). Gate Hermes #393: una siembra (lista oficial, re-siembra o
  // publicación) lo ESPERA antes de mandar sus seeds, para que un PATCH viejo
  // que termine después no los pise.
  const seedSaveInFlightRef = useRef<Promise<void> | null>(null);
  // Siembra en curso (lista oficial, re-siembra o publicación): mientras
  // viaja, ni el autoguardado ni "Guardar y continuar" mandan seeds, y los
  // controles que los disparan se bloquean (carrera inversa del gate #393).
  const [seedingInFlight, setSeedingInFlight] = useState(false);
  const seedingInFlightRef = useRef(false);
  // "Guardar y continuar" en curso: los campos se bloquean (lo tecleado ahí
  // se perdería al pasar a la siguiente categoría).
  const [walkSaving, setWalkSaving] = useState(false);
  const layoutInputRef = useRef<HTMLInputElement | null>(null);
  // Rank FMT: posición del jugador en el sistema oficial ligado a esta
  // categoría (por perfil). Permite mostrar la columna y auto-rankear.
  const [fmtPositions, setFmtPositions] = useState<Map<number, number>>(new Map());
  const [fmtSystemName, setFmtSystemName] = useState<string | null>(null);
  // R1 lista oficial por torneo: texto pegado/cargado, resultado del cruce y
  // posiciones oficiales (participant_id → posición) para la columna
  // informativa. La persistencia de la posición real es R2: vive en sesión.
  const [officialListText, setOfficialListText] = useState("");
  const [officialPreview, setOfficialPreview] = useState<{
    matched: Array<{ participant_id: number; name: string; position: number; seed: number }>;
    unmatched: Array<{ position: number; name: string | null }>;
    unlisted: Array<{ participant_id: number; name: string }>;
  } | null>(null);
  const [officialBusy, setOfficialBusy] = useState(false);
  const [listPositions, setListPositions] = useState<Map<number, number>>(new Map());
  const officialFileRef = useRef<HTMLInputElement | null>(null);
  // R2: lista guardada en servidor (sobrevive a la sesión) + drafts del
  // cruce manual (entry_id -> participant_id elegido en el select).
  const [savedEntries, setSavedEntries] = useState<OfficialListEntry[]>([]);
  const [matchDrafts, setMatchDrafts] = useState<Record<number, string>>({});
  const [matchingBusy, setMatchingBusy] = useState(false);
  // Walk del wizard: categorías ya guardadas en esta sesión (con S/R válido,
  // "pendiente" ya no puede definirse por jugadores sin rank).
  const savedDivisionsRef = useRef<Set<number>>(new Set());

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    setLoading(true);
    setError(null);
    try {
      const [tournamentResp, divisionsResp, participantsResp, registrationsResp, standingsResp] = await Promise.all([
        api.tournaments.get(token, tournamentId),
        api.tournaments.listDivisions(token, tournamentId),
        api.participants.list(token, tournamentId),
        api.registrations.get(token, tournamentId).catch(() => null),
        // Solo lectura: ¿quién ya jugó? Si falla, "Rank actual" usa la posición viva si existe.
        api.standings.get(token, tournamentId).catch(() => null),
      ]);
      setPlayedByParticipant(
        standingsResp
          ? new Map(standingsResp.data.ranking.map((row) => [row.participant_id, row.matches_played]))
          : null,
      );
      const nextDivisions = divisionsResp.data.divisions;
      const activeParticipants = participantsResp.data.participants.filter(
        (participant) => participant.current_status !== "dropped",
      );
      setTournament(tournamentResp.data.tournament);
      setDivisions(nextDivisions);
      setParticipants(activeParticipants);
      setRegistrationOrders(registrationsResp?.data.orders ?? []);
      // Eduardo 2026-09-24: logo del ORGANIZADOR (con el legacy credential_logo_url
      // de respaldo), nunca el logo/póster del torneo.
      setOrganizerLogoUrl(tournamentResp.data.tournament.organizer_logo_url ?? registrationsResp?.data.settings.credential_logo_url ?? null);
      setRankInputs({});
      setPointsInputs({});
      const requestedDivisionId = Number(searchParams.get("division"));
      const requested = nextDivisions.find((division) => division.id === requestedDivisionId);
      const participantDivisionIds = new Set(
        activeParticipants
          .map((participant) => participant.division_id)
          .filter((divisionId): divisionId is number => divisionId != null),
      );
      const fallbackDivision = nextDivisions.find((division) => participantDivisionIds.has(division.id)) ?? nextDivisions[0] ?? null;
      setSelectedDivisionId(requested?.id ?? fallbackDivision?.id ?? null);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar el ranking.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId, searchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedDivision = useMemo(
    () => divisions.find((division) => division.id === selectedDivisionId) ?? null,
    [divisions, selectedDivisionId],
  );

  // Carga el standings del sistema FMT ligado a la categoría activa (liga
  // exacta o paraguas) y mapea perfil → posición oficial.
  useEffect(() => {
    if (!token || Number.isNaN(tournamentId)) return;
    let cancelled = false;
    setFmtPositions(new Map());
    setFmtSystemName(null);
    void (async () => {
      try {
        const linksResp = await api.rankings.tournamentLinks(token, tournamentId);
        const links = linksResp.data.links;
        const link = selectOfficialRankingLink(links, selectedDivisionId);
        if (!link) {
          if (!cancelled) { setFmtPositions(new Map()); setFmtSystemName(null); }
          return;
        }
        const standingsResp = await api.rankings.standings(token, link.ranking_system_id, link.age_category_id);
        if (cancelled) return;
        const map = new Map<number, number>();
        standingsResp.data.standings.forEach((row: { player_profile_id: number; position: number }) => {
          map.set(row.player_profile_id, row.position);
        });
        setFmtPositions(map);
        setFmtSystemName(link.system_name ?? "Ranking oficial");
      } catch {
        if (!cancelled) { setFmtPositions(new Map()); setFmtSystemName(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [token, tournamentId, selectedDivisionId]);

  // R2: hidrata la lista oficial guardada del alcance actual; las
  // posiciones de los renglones ligados alimentan la columna informativa.
  const loadSavedEntries = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const response = await api.rankings.getOfficialList(token, tournamentId, selectedDivisionId);
      const entries = response.data.entries;
      setSavedEntries(entries);
      setMatchDrafts({});
      setListPositions(
        new Map(
          entries
            .filter((entry) => entry.matched_participant_id != null)
            .map((entry) => [entry.matched_participant_id as number, entry.position]),
        ),
      );
    } catch {
      setSavedEntries([]);
    }
  }, [token, tournamentId, selectedDivisionId]);

  useEffect(() => {
    void loadSavedEntries();
  }, [loadSavedEntries]);

  const anyWtn = useMemo(
    () => participants.some((participant) => participant.player?.wtn_tennis_id),
    [participants],
  );

  function fmtPositionFor(participant: TournamentParticipant): number | null {
    const profileId = participant.player_profile_id;
    if (profileId == null) return null;
    return fmtPositions.get(profileId) ?? null;
  }

  // Posición oficial para la columna: la lista cargada en este torneo manda;
  // si no hay, cae a la liga oficial (standings del sistema ligado).
  function officialPositionFor(participant: TournamentParticipant): number | null {
    return listPositions.get(participant.id) ?? fmtPositionFor(participant);
  }
  const showOfficialColumn = listPositions.size > 0 || fmtPositions.size > 0;

  const visibleParticipants = useMemo(() => {
    const scoped = selectedDivisionId
      ? participants.filter((participant) => participant.division_id === selectedDivisionId)
      : participants;
    return [...scoped].sort((a, b) => {
      const rankA = participantRankValue(a);
      const rankB = participantRankValue(b);
      if (rankA !== rankB) return rankA - rankB;
      return participantDisplayName(a).localeCompare(participantDisplayName(b), "es-MX");
    });
  }, [participants, selectedDivisionId]);

  // Eduardo 2026-09-24: en modo por puntos la tabla se reacomoda EN VIVO por
  // el seed generado (puntos del borrador), para que el admin vea el efecto
  // de cada captura sin tener que guardar y volver.
  const generatedRanks = useMemo(
    () =>
      seedsFromPoints(visibleParticipants, (participant) => {
        const points = parsePoints(pointsInputs[participant.id] ?? String(participant.ranking_points ?? 0));
        return points === "invalid" ? -1 : points;
      }),
    [pointsInputs, visibleParticipants],
  );

  // Modo recordado por categoría (Eduardo 2026-09-25): se deduce UNA vez por
  // categoría, al terminar de cargar; después manda el radio del admin.
  const inferredModeForDivisionRef = useRef<number | null | undefined>(undefined);
  useEffect(() => {
    if (loading) return;
    if (inferredModeForDivisionRef.current === selectedDivisionId) return;
    inferredModeForDivisionRef.current = selectedDivisionId;
    setMode((previous) => inferRankingMode(visibleParticipants, previous));
  }, [loading, selectedDivisionId, visibleParticipants]);

  // Seeds GUARDADOS que no salen de los puntos GUARDADOS (puestos a mano o de
  // antes de #389 a jugadores sin puntos). No depende de lo que se teclea.
  const seedsNotFromPointsCount = useMemo(() => seedsNotFromPoints(visibleParticipants), [visibleParticipants]);
  const seedsFollowPointsNow = useMemo(() => seedsFollowPoints(visibleParticipants), [visibleParticipants]);
  // ¿La categoría ya tiene partidos jugados? (tabla del torneo; null = no se supo)
  const divisionHasResults = useMemo<boolean | null>(() => {
    if (!playedByParticipant) return null;
    return visibleParticipants.some((participant) => (playedByParticipant.get(participant.id) ?? 0) > 0);
  }, [playedByParticipant, visibleParticipants]);

  const orderedParticipants = useMemo(() => {
    if (mode !== "points") return visibleParticipants;
    return [...visibleParticipants].sort(
      (a, b) => (generatedRanks.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (generatedRanks.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [generatedRanks, mode, visibleParticipants]);

  /** Items del PATCH /players/seeds en modo por puntos (puntos + seed
   * generado de cada inscrito visible); [] si nada cambió. */
  function buildPointsSeedItems(): ParticipantSeedItemInput[] {
    const items: ParticipantSeedItemInput[] = [];
    for (const participant of visibleParticipants) {
      const rawPoints = pointsInputs[participant.id] ?? String(participant.ranking_points ?? 0);
      const parsedPoints = parsePoints(rawPoints);
      if (parsedPoints === "invalid") {
        throw new Error("Los puntos deben ser números en 0 o mayores.");
      }
      const generatedRank = generatedRanks.get(participant.id) ?? null;
      // Igual que el guardado manual: no-op real si puntos/seed no
      // cambiaron; si cambian, current_rank nunca forma parte del payload.
      const payload = buildPointsAndSeedUpdatePayload(participant, parsedPoints, generatedRank);
      if (payload) {
        items.push({
          participant_id: participant.id,
          ...(payload.ranking_points !== undefined ? { ranking_points: payload.ranking_points } : {}),
          // La clave initial_rank solo viaja si el seed cambió; omitirla
          // deja el seed intacto en el backend.
          ...("initial_rank" in payload ? { initial_rank: payload.initial_rank } : {}),
        });
      }
    }
    return items;
  }

  /** Autoguardado al salir del campo de puntos (Eduardo 2026-09-24): guarda
   * puntos + seeds generados de la categoría y actualiza la tabla en sitio,
   * sin saltar de categoría (eso sigue siendo "Guardar y continuar"). */
  /** Marca un guardado de seeds en vuelo; devuelve la función que lo libera. */
  function trackSeedSave(): () => void {
    let release: () => void = () => undefined;
    const inFlight = new Promise<void>((resolve) => {
      release = () => resolve();
    });
    seedSaveInFlightRef.current = inFlight;
    return () => {
      release();
      if (seedSaveInFlightRef.current === inFlight) seedSaveInFlightRef.current = null;
    };
  }

  /** Antes de sembrar: pasa a manual (ningún autoguardado nuevo), suelta el
   * reintento pendiente y ESPERA el guardado en vuelo. */
  async function prepareSeeding(): Promise<void> {
    seedingInFlightRef.current = true;
    setSeedingInFlight(true);
    pendingAutosaveRef.current = null;
    setMode("manual");
    const inFlight = seedSaveInFlightRef.current;
    if (inFlight) await inFlight;
  }

  function finishSeeding(): void {
    seedingInFlightRef.current = false;
    setSeedingInFlight(false);
  }

  /** Refresca solo los inscritos (seeds nuevos) sin pasar por "Cargando…". */
  async function refreshParticipantsQuietly() {
    if (!token) return;
    try {
      const response = await api.participants.list(token, tournamentId);
      setParticipants(response.data.participants.filter((participant) => participant.current_status !== "dropped"));
      setRankInputs({});
      setPointsInputs({});
    } catch {
      // La tabla se actualiza en la siguiente carga; la siembra ya quedó guardada.
    }
  }

  async function autoSavePoints(changedParticipantId?: number) {
    if (!token || !tournament || mode !== "points" || seedingInFlightRef.current) return;
    if (saving) {
      // No se pierde: se re-intenta al terminar el guardado en curso.
      pendingAutosaveRef.current = { divisionId: selectedDivisionId };
      return;
    }
    if (changedParticipantId !== undefined) {
      // Entrar y salir de un campo sin cambiar el número no reescribe los
      // seeds de la categoría (Eduardo 2026-09-25).
      const raw = pointsInputs[changedParticipantId];
      if (raw === undefined) return;
      const parsed = parsePoints(raw);
      const saved = participants.find((participant) => participant.id === changedParticipantId)?.ranking_points ?? 0;
      if (parsed !== "invalid" && parsed === saved) {
        setPointsInputs((current) => {
          const next = { ...current };
          delete next[changedParticipantId];
          return next;
        });
        return;
      }
    }
    let items: ParticipantSeedItemInput[];
    try {
      items = buildPointsSeedItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Los puntos deben ser números en 0 o mayores.");
      return;
    }
    if (items.length === 0) return;
    const sentDrafts: PointsDraft = { ...pointsInputs };
    setSaving(true);
    const releaseSeedSave = trackSeedSave();
    setError(null);
    try {
      await api.participants.updateSeeds(token, tournamentId, { items });
      const byId = new Map(items.map((item) => [item.participant_id, item]));
      setParticipants((current) =>
        current.map((participant) => {
          const item = byId.get(participant.id);
          if (!item) return participant;
          return {
            ...participant,
            ...(item.ranking_points !== undefined ? { ranking_points: item.ranking_points } : {}),
            // El backend mueve tournament_rank junto con el seed: reflejarlo
            // evita un PATCH repetido en el siguiente blur.
            ...("initial_rank" in item ? { initial_rank: item.initial_rank ?? null, tournament_rank: item.initial_rank ?? null } : {}),
          };
        }),
      );
      setPointsInputs((current) => {
        // Solo se limpian los borradores que se mandaron tal cual: lo que se
        // tecleó mientras viajaba el PATCH se conserva (y se guarda después).
        const next = { ...current };
        byId.forEach((_, participantId) => {
          if (next[participantId] === sentDrafts[participantId]) delete next[participantId];
        });
        return next;
      });
      setGateRefreshKey((current) => current + 1);
      setMessage("Puntos guardados: la lista ya está acomodada por puntos.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudieron guardar los puntos." : "Error de red. Intenta otra vez.");
    } finally {
      setSaving(false);
      releaseSeedSave();
    }
  }


  useEffect(() => {
    const pending = pendingAutosaveRef.current;
    if (saving || !pending) return;
    pendingAutosaveRef.current = null;
    // Solo en la MISMA categoría y si quedó algo tecleado: tras el recorrido de
    // "Guardar y continuar" la categoría nueva no se toca sola.
    if (pending.divisionId !== selectedDivisionId || Object.keys(pointsInputs).length === 0) return;
    void autoSavePoints();
    // autoSavePoints lee el estado del render actual; solo importa el fin del guardado.
  }, [saving]);

  // Candado anti error humano (bug prod 2026-07-22: dos ranks 11 y dos 12
  // guardados → draw de 14 posiciones en vez de 16): detección EN VIVO de
  // duplicados en el modo manual. En modo puntos el rank generado es 1..N
  // por construcción y no puede duplicarse.
  const rankDraftEntries = useMemo(
    () =>
      visibleParticipants.map((participant) => {
        const parsed = parsePositiveRank(
          rankInputs[participant.id] ?? participantSeedInputValue(participant),
        );
        return {
          id: participant.id,
          name: participantDisplayName(participant),
          rank: typeof parsed === "number" ? parsed : null,
        };
      }),
    [visibleParticipants, rankInputs],
  );
  const duplicateRanks = useMemo(
    () => (mode === "manual" ? findDuplicateRanks(rankDraftEntries) : []),
    [mode, rankDraftEntries],
  );
  const duplicateRankSet = useMemo(
    () => new Set(duplicateRanks.map((group) => group.rank)),
    [duplicateRanks],
  );
  const hasDuplicateRanks = duplicateRanks.length > 0;
  // Huecos en la numeración: warning NO bloqueante (S/R es válido y hay
  // formatos donde solo se numera a los sembrados), pero un hueco suele
  // delatar una captura a medias.
  const rankGaps = useMemo(
    () => (mode === "manual" ? findRankGaps(rankDraftEntries) : []),
    [mode, rankDraftEntries],
  );

  // Cobertura por items ∪ ancla (re-gate #129, bloqueante 4): un participante
  // cubierto por el renglón de una orden pagada cuenta como pagado aunque no
  // sea el ancla de la orden.
  const ordersByParticipantId = useMemo(
    () => ordersByCoveredParticipant(registrationOrders),
    [registrationOrders],
  );

  function handleDownloadLayout() {
    const header = ["participante_id", "jugador", "categoria", "rank", "puntos", "pago"];
    const rows = visibleParticipants.map((participant) => {
      const order = ordersByParticipantId.get(participant.id);
      return [
        participant.id,
        participantDisplayName(participant),
        participantCategoryLabel(participant),
        rankInputs[participant.id] ?? participantSeedInputValue(participant),
        pointsInputs[participant.id] ?? participant.ranking_points ?? 0,
        paymentLabelForOrder(order),
      ];
    });
    const csv = [header, ...rows].map((row) => row.map((cell) => csvEscape(cell)).join(",")).join("\n");
    downloadBlob(
      `layout-ranking-${safeFilename(tournament?.name ?? "torneo")}-${selectedDivision ? safeFilename(selectedDivision.name) : "general"}.csv`,
      `\ufeff${csv}`,
      "text/csv;charset=utf-8",
    );
  }

  async function handleLayoutFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) throw new Error("El layout está vacío.");
      const headers = parseCsvLine(lines[0]).map((cell) => cell.trim().toLowerCase());
      const idIndex = headers.indexOf("participante_id");
      const rankIndex = headers.indexOf("rank");
      const pointsIndex = headers.indexOf("puntos");
      if (idIndex < 0) throw new Error("El layout debe incluir la columna participante_id.");
      const nextRanks: RankDraft = {};
      const nextPoints: PointsDraft = {};
      lines.slice(1).forEach((line) => {
        const cells = parseCsvLine(line);
        const participantId = Number(cells[idIndex]);
        if (!Number.isFinite(participantId)) return;
        if (rankIndex >= 0) nextRanks[participantId] = cells[rankIndex] ?? "";
        if (pointsIndex >= 0) nextPoints[participantId] = cells[pointsIndex] ?? "";
      });
      setRankInputs((current) => ({ ...current, ...nextRanks }));
      setPointsInputs((current) => ({ ...current, ...nextPoints }));
      setMessage("Layout cargado. Revisa los datos y presiona guardar para aplicarlos.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar el layout.");
    }
  }

  function handleDownloadPdf() {
    const byPoints = mode === "points";
    const unsorted = visibleParticipants.map((participant) => {
      const generatedRank = byPoints ? generatedRanks.get(participant.id) : null;
      const parsedRank = parsePositiveRank(rankInputs[participant.id] ?? participantSeedInputValue(participant));
      const rank = generatedRank ?? (typeof parsedRank === "number" ? parsedRank : null);
      const rawPoints = pointsInputs[participant.id] ?? String(participant.ranking_points ?? 0);
      const parsedPdfPoints = parsePoints(String(rawPoints));
      const points = typeof parsedPdfPoints === "number" ? parsedPdfPoints : 0;
      return {
        rank,
        points,
        row: {
          // Sin rank es un estado válido: S/R (feedback Eduardo 2026-07-15).
          rank: rank != null ? `#${rank}` : "S/R",
          player: participantDisplayName(participant),
          club: participantClubLabel(participant),
          category: participantCategoryLabel(participant),
          points: byPoints ? String(points) : null,
        },
      };
    });
    // Por puntos: la lista va ordenada por puntos y el Rank es el número de
    // lista (1..K) solo para quien tiene puntos; sin puntos, S/R. Con rank
    // manual se respeta el orden de la pantalla.
    const rows = byPoints
      ? [...unsorted]
          .sort((a, b) => b.points - a.points || (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER))
          .map((item, index) => ({ ...item.row, rank: pointsListRankLabel(item.points, index) }))
      : unsorted.map((item) => item.row);
    try {
      openPrintWindow({
        title: `inscritos-${safeFilename(tournament?.name ?? "torneo")}-${selectedDivision ? safeFilename(selectedDivision.name) : "general"}`,
        eyebrow: "Ranking y lista de inscritos",
        heading: tournament?.name ?? "Torneo",
        subtitle: `${selectedDivision?.name ?? "Lista general"} · ${rows.length} inscrito(s)`,
        bodyHtml: rankingSheetBodyHtml(rows, { withPoints: byPoints }),
        extraCss: RANKING_SHEET_CSS,
        organizerSlot: true,
        organizerLogoUrl,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo abrir la hoja de impresión.");
    }
  }

  function handleRandomRanks() {
    // Rank random (feedback Eduardo 2026-07-15): baraja a los inscritos de la
    // categoría y propone 1..N; el admin revisa y guarda.
    const shuffled = [...visibleParticipants];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const other = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[other]] = [shuffled[other], shuffled[index]];
    }
    // 1..N sobre la lista barajada: nunca produce duplicados.
    const next: RankDraft = buildSequentialRankDraft(shuffled.map((p) => p.id));
    setMode("manual");
    setRankInputs((current) => ({ ...current, ...next }));
    setMessage(`Rank random propuesto para ${selectedDivision?.name ?? "la lista"}. Revisa y guarda.`);
  }

  function handleUseFmtRanks() {
    // Auto-rankeo desde el ranking FMT: ordena por posición oficial y asigna
    // 1..N consecutivo; quien no aparece en el ranking queda S/R.
    const rows = visibleParticipants.map((participant) => ({
      id: participant.id,
      position: fmtPositionFor(participant),
    }));
    const positionedCount = rows.filter((row) => row.position != null).length;
    if (positionedCount === 0) {
      setError("Ningún inscrito de esta categoría aparece en el ranking oficial todavía.");
      return;
    }
    // 1..N consecutivo por posición oficial, S/R para el resto: sin duplicados.
    const next: RankDraft = buildFmtRankDraft(rows);
    setMode("manual");
    setRankInputs((current) => ({ ...current, ...next }));
    setMessage(`Rank FMT aplicado: ${positionedCount} con posición oficial, ${visibleParticipants.length - positionedCount} quedan S/R.`);
  }

  function parseOfficialListRows(text: string) {
    const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) throw new Error("Pega o carga la lista oficial primero.");
    const headerCells = parseCsvLine(lines[0]).map((cell) => cell.trim().toLowerCase());
    const posIndex = headerCells.findIndex((cell) => ["posicion", "posición", "position", "pos"].includes(cell));
    const nameIndex = headerCells.findIndex((cell) => ["nombre", "name", "jugador"].includes(cell));
    const fedIndex = headerCells.findIndex((cell) => ["id_federativo", "federation_id", "id federativo", "federativo"].includes(cell));
    const emailIndex = headerCells.findIndex((cell) => ["email", "correo"].includes(cell));
    if (posIndex < 0) {
      throw new Error("La lista necesita encabezado con la columna 'posicion' (usa la plantilla).");
    }
    const rows = lines.slice(1).map((line) => {
      const cells = parseCsvLine(line);
      const position = Number((cells[posIndex] ?? "").trim());
      return {
        position: Number.isFinite(position) && position >= 1 ? Math.trunc(position) : null,
        name: nameIndex >= 0 ? (cells[nameIndex] ?? "").trim() || null : null,
        federation_id: fedIndex >= 0 ? (cells[fedIndex] ?? "").trim() || null : null,
        email: emailIndex >= 0 ? (cells[emailIndex] ?? "").trim() || null : null,
      };
    }).filter((row) => row.position != null);
    if (rows.length === 0) throw new Error("Ningún renglón trae posición válida (números desde 1).");
    return rows;
  }

  function handleOfficialTemplate() {
    const csv = [
      "posicion,nombre,id_federativo,email",
      "1,Nombre Apellido,FMT-12345,jugador@correo.com",
      "2,Otra Jugadora,,otra@correo.com",
    ].join("\n");
    // BOM UTF-8 para que Excel abra el CSV con acentos correctos (igual que
    // handleDownloadLayout).
    downloadBlob("plantilla-ranking-oficial.csv", `\u{FEFF}${csv}`, "text/csv;charset=utf-8");
  }

  async function handleOfficialFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setOfficialListText(await file.text());
    setOfficialPreview(null);
  }

  async function runOfficialList(apply: boolean) {
    if (!token) return;
    const modeBefore = mode;
    if (apply) {
      // Una siembra a la vez: si ya viaja otra, no se lanza una segunda.
      if (seedingInFlightRef.current) return;
      // Seeds de la lista oficial = seeds manuales (igual que Rank random/FMT):
      // antes del POST, y después del guardado de puntos que esté en vuelo.
      await prepareSeeding();
    }
    setError(null);
    setMessage(null);
    setOfficialBusy(true);
    try {
      const rows = parseOfficialListRows(officialListText);
      const response = await api.rankings.officialList(token, tournamentId, {
        division_id: selectedDivisionId,
        apply,
        rows,
      });
      const data = response.data;
      setOfficialPreview(data);
      setListPositions(new Map(data.matched.map((entry) => [entry.participant_id, entry.position])));
      if (apply) {
        setMessage(
          `Seeds sembrados desde la lista oficial: ${data.matched.length} con posición` +
            `${data.unlisted.length > 0 ? `, ${data.unlisted.length} quedaron S/R` : ""}.`,
        );
        await load();
        await loadSavedEntries();
      }
    } catch (err) {
      // No se sembró nada: vuelve el modo que tenía el admin.
      if (apply) setMode(modeBefore);
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo procesar la lista oficial.");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setOfficialBusy(false);
      if (apply) finishSeeding();
    }
  }

  async function handleOfficialApply() {
    if (!officialPreview) return;
    const clears = officialPreview.unlisted.length;
    if (
      !(await askConfirm(
        `Se asignan seeds 1..${officialPreview.matched.length} según la posición oficial` +
          `${clears > 0 ? ` y ${clears} inscrito(s) que no aparecen en la lista quedan S/R` : ""}. ¿Continuar?`, { acceptLabel: "Sí, asignar seeds" }))
    ) {
      return;
    }
    void runOfficialList(true);
  }

  async function handleMatchEntry(entryId: number) {
    if (!token) return;
    const raw = matchDrafts[entryId] ?? "";
    const participantId = Number(raw);
    if (!raw || !Number.isFinite(participantId)) return;
    setMatchingBusy(true);
    setError(null);
    try {
      await api.rankings.matchOfficialListEntry(token, tournamentId, entryId, participantId);
      setMessage("Renglón ligado. Usa \u201cRe-sembrar seeds\u201d para actualizar el orden.");
      await loadSavedEntries();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo ligar el renglón.");
    } finally {
      setMatchingBusy(false);
    }
  }

  async function handleReapplySaved() {
    if (!token) return;
    const signal = currentSignal();
    if (
      !(await askConfirm(
        "Se re-siembran los seeds 1..N según la lista oficial guardada y los inscritos sin renglón quedan S/R. ¿Continuar?", { acceptLabel: "Sí, re-sembrar" }))
    ) {
      return;
    }
    const modeBefore = mode;
    // Una siembra a la vez: si ya viaja otra, no se lanza una segunda.
    if (seedingInFlightRef.current) return;
    await prepareSeeding();
    // Se cambió de torneo o categoría mientras se preparaba: no se re-siembra.
    if (signal?.aborted) return;
    setMatchingBusy(true);
    setError(null);
    try {
      const response = await api.rankings.reapplyOfficialList(token, tournamentId, selectedDivisionId);
      setMessage(
        `Seeds re-sembrados: ${response.data.matched.length} con posición` +
          `${response.data.unlisted.length > 0 ? `, ${response.data.unlisted.length} quedaron S/R` : ""}.`,
      );
      await load();
      await loadSavedEntries();
    } catch (err) {
      setMode(modeBefore);
      setError(err instanceof ApiError ? err.message : "No se pudo re-sembrar.");
    } finally {
      setMatchingBusy(false);
      finishSeeding();
    }
  }

  async function handleSaveAndContinue() {
    if (!token || !tournament || seedingInFlightRef.current) return;
    setSaving(true);
    setWalkSaving(true);
    const releaseSeedSave = trackSeedSave();
    setError(null);
    setMessage(null);
    try {
      // Guardado EN BLOQUE (candado del bug prod 2026-07-22): un solo PATCH
      // /players/seeds valida ranks únicos por división en el backend (409)
      // y permite intercambios A↔B sin duplicados transitorios.
      const items: ParticipantSeedItemInput[] = [];
      if (mode === "manual") {
        if (hasDuplicateRanks) {
          throw new Error(
            `${formatDuplicateRanksMessage(duplicateRanks)}. Corrige los ranks repetidos para poder guardar.`,
          );
        }
        for (const participant of visibleParticipants) {
          // El input edita el SEED: si el admin no lo tocó, conservar el seed
          // (initial_rank), no la posición viva — re-guardar a media
          // competencia no debe pisar los seeds con la tabla del momento.
          const rawRank = rankInputs[participant.id] ?? participantSeedInputValue(participant);
          const parsedRank = parsePositiveRank(rawRank);
          if (parsedRank === "invalid") {
            throw new Error("Los rankings deben ser números mayores a 0, o vacío para S/R (sin rank).");
          }
          // S/R es válido (feedback Eduardo 2026-07-15): vacío limpia el
          // seed. En ningún caso este paso escribe o borra current_rank.
          const payload = buildSeedUpdatePayload(participant, parsedRank);
          if (payload) {
            items.push({ participant_id: participant.id, initial_rank: payload.initial_rank });
          }
        }
      } else if (mode === "points") {
        items.push(...buildPointsSeedItems());
      }
      if (items.length > 0) {
        await api.participants.updateSeeds(token, tournamentId, { items });
      }
      setGateRefreshKey((current) => current + 1);
      // Walk por categorías (feedback Eduardo 2026-07-15, patrón del paso
      // Formato): al guardar, salta al ranking de la siguiente categoría con
      // jugadores que aún no se guarda en esta sesión; al terminar todas,
      // continúa a grupos.
      if (selectedDivisionId != null) savedDivisionsRef.current.add(selectedDivisionId);
      const divisionIdsWithPlayers = new Set(
        participants
          .map((participant) => participant.division_id)
          .filter((divisionId): divisionId is number => divisionId != null),
      );
      const nextDivision = divisions.find(
        (division) =>
          division.id !== selectedDivisionId &&
          divisionIdsWithPlayers.has(division.id) &&
          !savedDivisionsRef.current.has(division.id),
      );
      if (nextDivision) {
        // load() se re-ejecuta por el cambio de ?division=; marcar "cargando"
        // desde ya evita deducir el modo con datos de antes del guardado.
        setLoading(true);
        setSelectedDivisionId(nextDivision.id);
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set("division", String(nextDivision.id));
        setSearchParams(nextParams, { replace: true });
        setRankInputs({});
        setPointsInputs({});
        setMessage(`Ranking de ${selectedDivision?.name ?? "la categoría"} guardado. Ahora: ${nextDivision.name}.`);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setMessage("Ranking guardado en todas las categorías. Puedes continuar a grupos.");
      navigate(`/admin/tournaments/${tournament.id}/setup/groups${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo guardar el ranking.");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSaving(false);
      setWalkSaving(false);
      releaseSeedSave();
    }
  }

  if (Number.isNaN(tournamentId)) {
    return (
      <section className="card">
        <p className="form-error">ID de torneo inválido.</p>
      </section>
    );
  }

  // Candado por categoría: sin formato guardado no se opera Ranking.
  const guardDivision = selectedDivision;
  if (guardDivision && divisionNeedsFormat(guardDivision)) {
    return (
      <>
        
        <DivisionFormatGuardCard tournamentId={tournamentId} division={guardDivision} stepLabel="Ranking" />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <TournamentSetupProgress current={7} refreshKey={gateRefreshKey} tournamentStatus={tournament?.status} />
        <section className="card">
          <p className="muted">Cargando ranking…</p>
        </section>
      </>
    );
  }

  if (!tournament) {
    return (
      <>
        <TournamentSetupProgress current={7} refreshKey={gateRefreshKey} />
        <section className="card">
          <p className="form-error">{error ?? "Torneo no encontrado."}</p>
          <Link className="btn btn--ghost" to="/admin/tournaments">
            Volver
          </Link>
        </section>
      </>
    );
  }

  // Siguiente categoría del recorrido (misma regla que handleSaveAndContinue).
  const barDivisionIdsWithPlayers = new Set(
    participants
      .map((participant) => participant.division_id)
      .filter((divisionId): divisionId is number => divisionId != null),
  );
  const barNextDivision = divisions.find(
    (division) =>
      division.id !== selectedDivisionId &&
      barDivisionIdsWithPlayers.has(division.id) &&
      !savedDivisionsRef.current.has(division.id),
  ) ?? null;
  const anyClub = visibleParticipants.some((participant) => Boolean(participant.player?.club_name));

  return (
    <div className="wiz-step wiz-step--ranking">
      <TournamentSetupProgress
        current={7}
        refreshKey={gateRefreshKey}
        tournamentStatus={tournament?.status}
        hero={(
          <section className="wiz-hero wiz-hero--navy setup-ranking-hero">
            <div>
              <p className="eyebrow">{`Paso 7 de 10 · ${selectedDivision?.name ?? tournament.name}`}</p>
              <h1>Ranking</h1>
              <p className="wiz-hero__lead">
                Ordena a los jugadores para sembrar los grupos y el cuadro. Completa todas las categorías para continuar.
              </p>
            </div>
            <div className="wiz-hero__actions">
              <button className="btn btn--ghost" type="button" onClick={handleDownloadPdf}>
                Ranking en PDF
              </button>
            </div>
          </section>
        )}
      />

      {error ? <p className="form-error">{error}</p> : null}
      {message ? <p className="form-success">{message}</p> : null}

      <section className="card wiz-card ranking-mode-card">
        <div className="wiz-card__head">
          <h2>Cómo se siembra</h2>
          <p>¿Cómo quieres ordenar jugadores?</p>
        </div>
        <div className="ranking-mode-grid">
          <label className={`ranking-mode-option${mode === "manual" ? " is-selected" : ""}`}>
            <input
              type="radio"
              name="ranking-mode"
              value="manual"
              checked={mode === "manual"}
              onChange={() => setMode("manual")}
              disabled={seedingInFlight}
            />
            <span>
              <span className="hint-label">
                <strong>Por seed manual</strong>
                <FieldHint>
                  Edita el rank directamente si el organizador ya tiene su lista.
                </FieldHint>
              </span>
              <small>Tú pones el #1, #2, #3…</small>
            </span>
          </label>
          <label className={`ranking-mode-option${mode === "points" ? " is-selected" : ""}`}>
            <input
              type="radio"
              name="ranking-mode"
              value="points"
              checked={mode === "points"}
              onChange={() => setMode("points")}
              disabled={seedingInFlight}
            />
            <span>
              <span className="hint-label">
                <strong>Por puntos</strong>
                <FieldHint>
                  Captura puntos y el sistema genera el rank automáticamente.
                </FieldHint>
              </span>
              <small>Capturas puntos (acepta decimales) y el sistema ordena.</small>
            </span>
          </label>

        </div>
      </section>

      <section className="card wiz-card rk-official-card">
        <div className="wiz-card__head">
          <h2>Ranking oficial y sistema de puntos</h2>
        </div>
        <div className="rk-official-grid">
        <div className="rk-tile official-list-card">
        <h3 className="hint-label rk-tile__title">
          Cargar lista oficial
          <FieldHint label="¿Cómo funciona la lista oficial?">
            Pega o carga el CSV del ranking oficial (posición, nombre, ID
            federativo, email). El sistema cruza contra tus inscritos
            {selectedDivision ? ` de ${selectedDivision.name}` : ""} y al
            aplicar asigna seeds 1..N en el orden de la posición oficial;
            quien no aparezca queda S/R. La posición real se muestra como
            columna informativa.
          </FieldHint>
        </h3>
        <p className="muted rk-tile__lead">Pega o sube la lista (posición, nombre, ID federativo, correo) y cruza con tus inscritos.</p>
        {tournament.is_federation_official && fmtPositions.size === 0 && listPositions.size === 0 ? (
          <p className="muted">
            Este torneo está marcado como oficial de Federación y aún no tiene
            ranking oficial cargado: usa la plantilla y pega la lista aquí.
          </p>
        ) : null}
        <div className="rk-drop">
        <textarea
          rows={4}
          value={officialListText}
          placeholder={"posicion,nombre,id_federativo,email\n1,Nombre Apellido,FMT-12345,jugador@correo.com"}
          onChange={(event) => {
            setOfficialListText(event.target.value);
            setOfficialPreview(null);
          }}
          disabled={officialBusy}
        />
        </div>
        <div className="form__actions rk-tile__actions">
          <button className="btn btn--ghost" type="button" onClick={handleOfficialTemplate}>
            Descargar plantilla
          </button>
          <button className="btn btn--ghost" type="button" onClick={() => officialFileRef.current?.click()} disabled={officialBusy}>
            Cargar archivo CSV
          </button>
          <button className="btn btn--ghost" type="button" onClick={() => void runOfficialList(false)} disabled={officialBusy || !officialListText.trim()}>
            {officialBusy ? "Cruzando…" : "Vista previa del cruce"}
          </button>
          <button
            className="btn"
            type="button"
            onClick={handleOfficialApply}
            disabled={officialBusy || saving || seedingInFlight || !officialPreview || officialPreview.matched.length === 0}
            title={!officialPreview ? "Primero haz la vista previa del cruce." : undefined}
          >
            Sembrar seeds desde la lista
          </button>
          <input
            ref={officialFileRef}
            type="file"
            accept=".csv,text/csv"
            className="ranking-layout-file-input"
            onChange={(event) => void handleOfficialFileChange(event)}
          />
        </div>
        {officialPreview ? (
          <div className="official-list-summary">
            <p>
              <strong>{officialPreview.matched.length}</strong> inscrito(s) cruzaron con la lista
              {officialPreview.unlisted.length > 0
                ? `; ${officialPreview.unlisted.length} no aparecen y quedarán S/R`
                : ""}
              {officialPreview.unmatched.length > 0
                ? `; ${officialPreview.unmatched.length} renglón(es) de la lista no corresponden a inscritos`
                : ""}
              .
            </p>
            {officialPreview.unlisted.length > 0 ? (
              <p className="muted">
                Sin posición oficial: {officialPreview.unlisted.slice(0, 8).map((row) => row.name).join(", ")}
                {officialPreview.unlisted.length > 8 ? "…" : ""}
              </p>
            ) : null}
          </div>
        ) : null}
        {savedEntries.length > 0 ? (
          <div className="official-list-summary">
            <p>
              Lista oficial guardada: <strong>{savedEntries.length}</strong> renglón(es),{" "}
              {savedEntries.filter((entry) => entry.matched_participant_id != null).length} ligado(s) a inscritos.
            </p>
            {savedEntries.some((entry) => entry.matched_participant_id == null) ? (
              <ul className="official-unmatched-list">
                {savedEntries
                  .filter((entry) => entry.matched_participant_id == null)
                  .slice(0, 12)
                  .map((entry) => {
                    const takenIds = new Set(
                      savedEntries
                        .map((item) => item.matched_participant_id)
                        .filter((id): id is number => id != null),
                    );
                    const candidates = visibleParticipants.filter(
                      (participant) => !takenIds.has(participant.id),
                    );
                    return (
                      <li key={entry.id}>
                        <span>
                          #{entry.position} · {entry.name ?? "(sin nombre)"}
                        </span>
                        <select className="t123-select"
                          value={matchDrafts[entry.id] ?? ""}
                          onChange={(event) =>
                            setMatchDrafts((current) => ({ ...current, [entry.id]: event.target.value }))
                          }
                          disabled={matchingBusy}
                        >
                          <option value="">Ligar a inscrito…</option>
                          {candidates.map((participant) => (
                            <option key={participant.id} value={participant.id}>
                              {participantDisplayName(participant)}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn--ghost btn--compact"
                          onClick={() => void handleMatchEntry(entry.id)}
                          disabled={matchingBusy || !(matchDrafts[entry.id] ?? "")}
                        >
                          Ligar
                        </button>
                      </li>
                    );
                  })}
              </ul>
            ) : null}
            <div className="form__actions">
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => void handleReapplySaved()}
                disabled={matchingBusy || saving || seedingInFlight}
              >
                Re-sembrar seeds desde la lista guardada
              </button>
            </div>
          </div>
        ) : null}
        </div>
        <div className="rk-tile official-advanced">
          <OfficialRankingCard
            tournamentId={tournament.id}
            divisionId={selectedDivisionId}
            divisionName={selectedDivision?.name ?? null}
            embedded
            seedingDisabled={saving || seedingInFlight}
            onSeeding={async (phase) => {
              if (phase === "start") {
                // La publicación siembra seeds "manuales": que ningún
                // guardado por puntos los pise (espera el que esté en vuelo).
                modeBeforeSeedingRef.current = mode;
                await prepareSeeding();
              } else if (phase === "failed") {
                finishSeeding();
                if (modeBeforeSeedingRef.current) setMode(modeBeforeSeedingRef.current);
                modeBeforeSeedingRef.current = null;
              } else {
                finishSeeding();
                modeBeforeSeedingRef.current = null;
                // Sin recargar toda la página: el mensaje de la tarjeta y
                // "Avanzado" abierto se conservan.
                void refreshParticipantsQuietly();
              }
            }}
          />
        </div>
        </div>
      </section>

      <section className="card wiz-card ranking-table-card">
        <div className="card__header-row ranking-table-card__header">
          <div className="wiz-card__head">
            <h2 className="hint-label">
              {visibleParticipants.length} inscrito(s)
              <FieldHint>
                Aquí editas el SEED (orden de siembra para draw y grupos).
                “Ranking oficial” es la posición en la lista/liga oficial, solo
                informativa. “Rank actual” es la posición viva de la tabla del
                torneo; antes de que haya tabla (antes de jugar) muestra el seed.
                No se edita en este paso.
              </FieldHint>
            </h2>
          </div>
          <div className="ranking-table-actions">
            <button className="btn btn--ghost" type="button" onClick={handleDownloadLayout}>
              Descargar layout
            </button>
            <button className="btn btn--ghost" type="button" onClick={() => layoutInputRef.current?.click()}>
              Cargar layout
            </button>
            <button className="btn btn--ghost" type="button" onClick={handleRandomRanks}>
              🎲 Rank random
            </button>
            {fmtPositions.size > 0 ? (
              <button
                className="btn btn--ghost"
                type="button"
                title={`Seeds 1..N según la posición en ${fmtSystemName ?? "la liga oficial"}; revisa y guarda.`}
                onClick={handleUseFmtRanks}
              >
                Sembrar desde liga oficial
              </button>
            ) : null}
            <input
              ref={layoutInputRef}
              type="file"
              accept=".csv,text/csv"
              className="ranking-layout-file-input"
              onChange={(event) => void handleLayoutFileChange(event)}
            />
          </div>
        </div>

        {hasDuplicateRanks ? (
          <p className="form-error" role="alert">
            {formatDuplicateRanksMessage(duplicateRanks)}. Cada rank debe ser
            único en la categoría; corrige los repetidos para poder guardar.
          </p>
        ) : null}
        {mode === "points" && !seedsFollowPointsNow && seedsNotFromPointsCount > 0 ? (
          <div className="ranking-gaps-hint ranking-points-sync" data-no-toast>
            <p className="muted">
              {seedsNotFromPointsCount} jugador{seedsNotFromPointsCount === 1 ? "" : "es"} todavía tiene
              {seedsNotFromPointsCount === 1 ? "" : "n"} guardado un seed que no sale de sus puntos. Al guardar por
              puntos, el seed se genera de los puntos y quien no tiene puntos queda S/R.
            </p>
            <button
              type="button"
              className="btn btn--ghost btn--compact"
              onClick={() => void autoSavePoints()}
              disabled={saving || walkSaving}
            >
              Guardar orden por puntos
            </button>
          </div>
        ) : null}
        {!hasDuplicateRanks && rankGaps.length > 0 ? (
          <p className="muted ranking-gaps-hint">
            Aviso: hay huecos en la numeración (falta{" "}
            {rankGaps.slice(0, 8).join(", ")}
            {rankGaps.length > 8 ? "…" : ""}). No bloquea el guardado — S/R es
            válido — pero revisa que no sea una captura a medias.
          </p>
        ) : null}
        {visibleParticipants.length === 0 ? (
          <p className="muted">No hay jugadores inscritos en esta categoría todavía.</p>
        ) : (
          <div className="ranking-table-wrap">
            <table className="ranking-table">
              <thead>
                <tr>
                  <th>{mode === "points" ? "Seed generado" : "Seed"}</th>
                  <th>Jugador</th>
                  {anyClub ? <th>Club</th> : null}
                  {anyWtn ? <th>WTN</th> : null}
                  {showOfficialColumn ? <th>Ranking oficial</th> : null}
                  <th>Pago</th>
                  {mode === "points" ? <th>Puntos</th> : null}
                  <th>Rank actual</th>
                </tr>
              </thead>
              <tbody>
                {orderedParticipants.map((participant) => {
                  const rankValue = rankInputs[participant.id] ?? participantSeedInputValue(participant);
                  const pointsValue = pointsInputs[participant.id] ?? String(participant.ranking_points ?? 0);
                  const generatedRank = generatedRanks.get(participant.id) ?? null;
                  const paymentOrder = ordersByParticipantId.get(participant.id);
                  const paymentKey = paymentKeyForOrder(paymentOrder);
                  const parsedRankValue = parsePositiveRank(rankValue);
                  const isDuplicated =
                    mode === "manual" &&
                    typeof parsedRankValue === "number" &&
                    duplicateRankSet.has(parsedRankValue);
                  const rankActual = rankActualLabel(
                    participant,
                    mode === "points" ? generatedRank : typeof parsedRankValue === "number" ? parsedRankValue : null,
                    mode,
                    divisionHasResults,
                  );
                  return (
                    <tr key={participant.id}>
                      <td data-label={mode === "points" ? "Seed generado" : "Seed"}>
                        {mode === "points" ? (
                          <span className={`ranking-generated-rank rk-seed${generatedRank != null ? "" : " rk-seed--sr"}`}>{generatedRank != null ? `#${generatedRank}` : "S/R"}</span>
                        ) : (
                          <input
                            className={`ranking-number-input${isDuplicated ? " ranking-number-input--error" : ""}`}
                            type="number"
                            min={1}
                            value={rankValue}
                            placeholder="S/R"
                            aria-invalid={isDuplicated || undefined}
                            title={
                              isDuplicated
                                ? "Rank repetido en esta categoría: corrígelo para poder guardar."
                                : "Vacío = sin rank (S/R): el draw lo acomoda en orden aleatorio."
                            }
                            onChange={(event) => setRankInputs((current) => ({
                              ...current,
                              [participant.id]: event.target.value,
                            }))}
                            disabled={walkSaving || seedingInFlight}
                          />
                        )}
                      </td>
                      <td data-label="Jugador">
                        <strong>{participantDisplayName(participant)}</strong>
                      </td>
                      {anyClub ? (
                        <td data-label="Club">{participant.player?.club_name || "—"}</td>
                      ) : null}
                      {anyWtn ? (
                        <td data-label="WTN">{participant.player?.wtn_tennis_id ?? "—"}</td>
                      ) : null}
                      {showOfficialColumn ? (
                        <td data-label="Ranking oficial">
                          {officialPositionFor(participant) != null ? `#${officialPositionFor(participant)}` : "—"}
                        </td>
                      ) : null}
                      <td data-label="Pago">
                        <span className={`ranking-payment-badge ranking-payment-badge--${paymentKey}`}>
                          {paymentLabelForOrder(paymentOrder)}
                        </span>
                      </td>
                      {mode === "points" ? (
                        <td data-label="Puntos">
                          <input
                            className="ranking-number-input"
                            type="text"
                            inputMode="decimal"
                            step="0.01"
                            value={pointsValue}
                            title="Al salir del campo se guarda y la lista se reacomoda por puntos."
                            disabled={walkSaving || seedingInFlight}
                            onChange={(event) => setPointsInputs((current) => ({
                              ...current,
                              [participant.id]: event.target.value,
                            }))}
                            onBlur={() => void autoSavePoints(participant.id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                event.currentTarget.blur();
                              }
                            }}
                          />
                        </td>
                      ) : null}
                      <td data-label="Rank actual">
                        {rankActual.rank}
                        {rankActual.note ? <small className="muted ranking-rank-note"> · {rankActual.note}</small> : null}
                        {rankActual.points ? ` · ${rankActual.points}` : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted rk-sr-note">S/R = sin ranking: se sortea entre los no sembrados.</p>
      </section>

      {hasDuplicateRanks ? (
        <p className="form-error">
          No puedes guardar con ranks repetidos: {formatDuplicateRanksMessage(duplicateRanks)}.
        </p>
      ) : null}
      <div className="wiz-bottombar wiz-bottombar--brand fmt-bottombar rk-bottombar">
        <Link className="wiz-bottombar__back" to={`/admin/tournaments/${tournament.id}/setup/format${selectedDivisionId ? `?division=${selectedDivisionId}` : ""}`}>
          ‹ Volver a Formato
        </Link>
        <div className="fmt-bottombar__actions">
          <button
            className="wiz-bottombar__next fmt-bottombar__next"
            type="button"
            disabled={saving || seedingInFlight || hasDuplicateRanks}
            title={hasDuplicateRanks ? "Corrige los ranks repetidos para poder guardar." : undefined}
            onClick={() => void handleSaveAndContinue()}
          >
            <span className="fmt-bottombar__label">
              {saving
                ? "Guardando…"
                : barNextDivision
                  ? `Guardar y continuar con ${barNextDivision.name} ›`
                  : "Guardar y continuar a Grupos ›"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
